import { useFinancialPlanStore } from '../../store/financialPlanStore'
import { CheckCircle, AlertCircle, User, Wallet, TrendingUp, Receipt, Shield } from 'lucide-react'

export function ReviewStep() {
  const { currentPlan } = useFinancialPlanStore()
  
  if (!currentPlan) return null
  
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value)
  
  const totalAccounts = currentPlan.accounts.reduce((sum, acc) => sum + acc.balance, 0)
  const totalIncome = currentPlan.income.reduce((sum, inc) => sum + inc.amount, 0)
  const totalExpenses = currentPlan.expenses.reduce((sum, exp) => sum + exp.amount, 0)
  
  const client = currentPlan.familyMembers.find(m => m.relationship === 'client')
  const hasMinimumData = client && currentPlan.accounts.length > 0 && currentPlan.income.length > 0
  
  return (
    <div className="space-y-6">
      {hasMinimumData ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-green-900 mb-1">Ready to Generate Plan</h3>
              <p className="text-sm text-green-700">
                You've provided the minimum required information. Review your entries below and click "Complete Setup" to generate your financial plan.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-900 mb-1">Incomplete Information</h3>
              <p className="text-sm text-yellow-700">
                Please ensure you've added: (1) Personal Information, (2) At least one account, and (3) At least one income source.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Personal Information Summary */}
      <div className="card">
        <div className="flex items-center mb-4">
          <User className="h-5 w-5 text-primary-600 mr-2" />
          <h3 className="font-semibold text-lg">Personal Information</h3>
        </div>
        <div className="space-y-2">
          <p><span className="font-medium">Client:</span> {currentPlan.clientName}</p>
          <p><span className="font-medium">Email:</span> {currentPlan.email}</p>
          <p><span className="font-medium">Family Members:</span> {currentPlan.familyMembers.length}</p>
          {client && (
            <p><span className="font-medium">Retirement Age:</span> {client.retirementAge || 'Not set'}</p>
          )}
        </div>
      </div>
      
      {/* Accounts Summary */}
      <div className="card">
        <div className="flex items-center mb-4">
          <Wallet className="h-5 w-5 text-primary-600 mr-2" />
          <h3 className="font-semibold text-lg">Assets & Accounts</h3>
        </div>
        <div className="space-y-2">
          <p><span className="font-medium">Investment Accounts:</span> {currentPlan.accounts.length}</p>
          <p><span className="font-medium">Total Portfolio Value:</span> {formatCurrency(totalAccounts)}</p>
          <p><span className="font-medium">Real Estate Properties:</span> {currentPlan.realEstate.length}</p>
        </div>
        {currentPlan.accounts.length === 0 && (
          <p className="text-sm text-red-600 mt-2">⚠️ Add at least one account</p>
        )}
      </div>
      
      {/* Income Summary */}
      <div className="card">
        <div className="flex items-center mb-4">
          <TrendingUp className="h-5 w-5 text-primary-600 mr-2" />
          <h3 className="font-semibold text-lg">Income Sources</h3>
        </div>
        <div className="space-y-2">
          <p><span className="font-medium">Income Sources:</span> {currentPlan.income.length}</p>
          <p><span className="font-medium">Total Annual Income:</span> {formatCurrency(totalIncome)}</p>
        </div>
        {currentPlan.income.length === 0 && (
          <p className="text-sm text-red-600 mt-2">⚠️ Add at least one income source</p>
        )}
      </div>
      
      {/* Expenses Summary */}
      <div className="card">
        <div className="flex items-center mb-4">
          <Receipt className="h-5 w-5 text-primary-600 mr-2" />
          <h3 className="font-semibold text-lg">Expenses</h3>
        </div>
        <div className="space-y-2">
          <p><span className="font-medium">Expense Categories:</span> {currentPlan.expenses.length}</p>
          <p><span className="font-medium">Total Annual Expenses:</span> {formatCurrency(totalExpenses)}</p>
          {totalIncome > 0 && totalExpenses > 0 && (
            <p>
              <span className="font-medium">Savings Rate:</span>{' '}
              <span className={totalIncome > totalExpenses ? 'text-green-600' : 'text-red-600'}>
                {((1 - totalExpenses / totalIncome) * 100).toFixed(1)}%
              </span>
            </p>
          )}
        </div>
      </div>
      
      {/* Risk Profile Summary */}
      <div className="card">
        <div className="flex items-center mb-4">
          <Shield className="h-5 w-5 text-primary-600 mr-2" />
          <h3 className="font-semibold text-lg">Risk Profile</h3>
        </div>
        {currentPlan.riskProfile ? (
          <div className="space-y-2">
            <p><span className="font-medium">Risk Tolerance:</span> {currentPlan.riskProfile.tolerance}</p>
            <p>
              <span className="font-medium">Recommended Allocation:</span>{' '}
              {currentPlan.riskProfile.stocksPercent}% Stocks / {currentPlan.riskProfile.bondsPercent}% Bonds
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-600">Risk assessment not completed (optional)</p>
        )}
      </div>
      
      {/* Next Steps */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
        <h3 className="font-semibold mb-3">What Happens Next?</h3>
        <ol className="space-y-2 text-sm">
          <li className="flex items-start">
            <span className="font-medium mr-2">1.</span>
            <span>We'll run 1,000 Monte Carlo simulations of your financial future</span>
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">2.</span>
            <span>Calculate your retirement success probability</span>
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">3.</span>
            <span>Generate interactive charts and projections</span>
          </li>
          <li className="flex items-start">
            <span className="font-medium mr-2">4.</span>
            <span>Provide personalized recommendations and what-if scenarios</span>
          </li>
        </ol>
      </div>
    </div>
  )
}
